import { useRef, useState } from 'react'
import { FomLogo } from './Bausteine'
import { konferenz } from '@/data/inhalte'

/** Einfacher Zufallsgenerator mit Startwert - gleiches Muster fuer gleichen Namen */
function muster(text: string, anzahl: number): boolean[] {
  let h = 2166136261
  for (const ch of text) h = Math.imul(h ^ ch.charCodeAt(0), 16777619)
  return Array.from({ length: anzahl }, () => {
    h = Math.imul(h ^ (h >>> 15), 2246822519) ^ Math.imul(h ^ (h >>> 13), 3266489917)
    return ((h >>> 16) & 3) !== 0
  })
}

/** Persoenliches Wabenmuster aus Name, Branche und Funktion (rein dekorativ) */
function Wabenmuster({ text }: Readonly<{ text: string }>) {
  const sp = 6, zl = 4, r = 5.2
  const an = muster(text || 'FOM', sp * zl)
  return (
    <svg aria-hidden="true" viewBox="0 0 64 40" className="h-11 w-auto">
      {an.map((ja, i) => {
        const s = i % sp, z = Math.floor(i / sp)
        const cx = 6 + s * 9.6 + (z % 2) * 4.8, cy = 6 + z * 8.4
        const p = Array.from({ length: 6 }, (_, k) => {
          const a = (Math.PI / 180) * (60 * k - 90)
          return `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`
        }).join(' ')
        return <polygon key={i} points={p} fill={ja ? '#22C3AC' : 'rgba(255,255,255,.12)'} className="transition-[fill] duration-300" />
      })}
    </svg>
  )
}

/** Konferenzausweis, der sich beim Ausfuellen des Formulars live fuellt und sich zur Maus neigt. */
export default function Ausweis({ name, organisation, branche, funktion, angemeldet, format = konferenz.format, ohneBranche = false }: Readonly<{ name: string; organisation: string; branche: string; funktion: string; angemeldet: boolean; format?: string; ohneBranche?: boolean }>) {
  const ref = useRef<HTMLDivElement>(null)
  const [neigung, setNeigung] = useState({ x: 0, y: 0, gx: 50, gy: 30 })

  const bewegen = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const r = ref.current!.getBoundingClientRect()
    const px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height
    setNeigung({ x: (0.5 - py) * 12, y: (px - 0.5) * 14, gx: px * 100, gy: py * 100 })
  }

  return (
    <div ref={ref} onPointerMove={bewegen} onPointerLeave={() => setNeigung({ x: 0, y: 0, gx: 50, gy: 30 })} className="mx-auto w-full max-w-[340px] [perspective:1100px]">
      {/* Band */}
      <div aria-hidden="true" className="mx-auto h-16 w-9 bg-[repeating-linear-gradient(90deg,#001E2B_0_6px,#017C76_6px_12px)] opacity-90 [clip-path:polygon(15%_0,85%_0,100%_100%,0_100%)]" />
      <div
        className="relative -mt-1 overflow-hidden rounded-[26px] text-white shadow-[0_40px_80px_rgba(0,19,28,.45)] transition-transform duration-200 ease-out verlauf-konferenz"
        style={{ transform: `rotateX(${neigung.x}deg) rotateY(${neigung.y}deg)` }}
      >
        {/* Glanz folgt der Maus */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 transition-opacity" style={{ background: `radial-gradient(260px circle at ${neigung.gx}% ${neigung.gy}%, rgba(255,255,255,.18), transparent 60%)` }} />
        <div className="relative p-6 sm:p-7">
          <div aria-hidden="true" className="mx-auto h-2.5 w-16 rounded-full bg-konf-tief/70 shadow-[inset_0_1px_2px_rgba(0,0,0,.4)]" />
          <div className="mt-6 flex items-center justify-between">
            <FomLogo className="h-8" />
            <span className="rounded-md border border-white/30 px-2 py-1 text-[10.5px] font-bold uppercase tracking-[0.28em]">Zugang</span>
          </div>
          <p className="mt-7 font-display text-[12.5px] font-bold uppercase tracking-[0.24em] text-konf-hell">{konferenz.marke}</p>
          <p className="mt-1 font-display text-[21px] font-bold leading-tight">{konferenz.titel.join(' ')}</p>

          <div className="mt-7 border-t border-white/20 pt-5">
            <p className="text-[10.5px] uppercase tracking-[0.28em] text-white/55">Name</p>
            <p className="mt-1 min-h-[1.15em] break-words font-display text-[28px] font-bold leading-[1.1]">
              {name || <span className="text-white/35">Ihr Name</span>}
            </p>
            <dl className="mt-4 grid gap-3">
              {([['Unternehmen / Verband', organisation, 'Ihr Unternehmen / Verband'], ['Branche', branche, 'Ihre Branche'], ['Funktion', funktion, 'Ihre Funktion']] as const)
                .filter(([titel]) => !(ohneBranche && titel === 'Branche'))
                .map(([titel, wert, leer]) => (
                <div key={titel}>
                  <dt className="text-[10.5px] uppercase tracking-[0.28em] text-white/55">{titel}</dt>
                  <dd className="mt-0.5 min-h-[1.35em] break-words font-display text-[16px] font-bold leading-snug">
                    {wert || <span className="font-normal text-white/35">{leer}</span>}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="mt-5 flex items-end justify-between gap-4 rounded-2xl bg-konf-tief/50 p-4">
            <div>
              <p className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] text-white/60">
                <span className="h-1.5 w-1.5 rounded-full bg-[#E5322D] on-air" />{format}
              </p>
              <p className="mt-1.5 font-display text-[17px] font-bold leading-tight">{konferenz.datumKurz}</p>
              <p className="text-[14px] text-white/75">{konferenz.uhrzeit}</p>
            </div>
            <Wabenmuster text={`${name}|${organisation}|${branche}|${funktion}`} />
          </div>
        </div>
        {angemeldet && (
          <div role="status" className="stempel absolute right-5 top-[42%] rotate-[-12deg] rounded-lg border-[2.5px] border-konf-hell bg-konf-nacht/40 px-3.5 py-1.5 font-display text-[15px] font-bold uppercase tracking-[0.22em] text-konf-hell backdrop-blur-sm">
            Angemeldet
          </div>
        )}
      </div>
    </div>
  )
}
